import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormComponent, IFormDialogData } from 'src/app/shared/form/form.component';
import { CalibrationEquipment, CalibrationEquipmentOwner } from 'src/app/models/calibrationEquipment.model';
import { TextFormField, DateFormField, SingleSelectionFormField, HierarchicalSelectionFormField, NumberFormField, MultiSelectionFormField } from 'src/app/shared/form/formField.model';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { CalibrationEquipmentService } from 'src/app/services/calibration-equipment.service';
import { TypeService } from 'src/app/services/type.service';
import { EquipmentStatus } from 'src/app/enums/equipmentStatus.enum';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { forkJoin } from 'rxjs';
import { Lookup } from 'src/app/models/lookup.model';
import { CalibrationFrequency } from 'src/app/enums/calibrationFrequency.enum';
import { OperatorTypeEnum } from 'src/app/enums/operatorType.enum';
import { OrganizationLevel } from 'src/app/models/organizationLevel.model';
import { OrgLevelTypeEnum } from 'src/app/enums/orgLevelType.enum';
import { toOrganizationTree } from 'src/app/_helpers/org-tree-helper';
import { UserService } from 'src/app/services/user.service';
import { ILookup } from 'src/app/shared/form/lookup.model';

@Component({
  standalone: false,
  selector: 'app-calibration-equipment-dialog',
  templateUrl: './../../shared/form/form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/form/form.component.scss']
})
export class CalibrationEquipmentDialogComponent extends FormComponent<CalibrationEquipment> implements OnInit {
  organizationLevels: Lookup[] = [];
  users: ILookup[] = [];
  private _originalLastNotification?: string;

  constructor(
    dialogRef: MatDialogRef<CalibrationEquipmentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IFormDialogData,
    calibrationEquipmentService: CalibrationEquipmentService,
    protected injector: Injector,
    private typeService: TypeService,
    private organizationLevelService: OrganizationLevelService,
    private userService: UserService
  ) {
    super(dialogRef, calibrationEquipmentService, data, 'Calibration Equipment', injector);
  }

  ngOnInit(): void {
    const orgPayload = JSON.stringify({
      OperatorTypeEnum: JSON.stringify({
        Condition: OperatorTypeEnum.Equal,
        Field: nameOf<OrganizationLevel>(x => x.orgLevelType),
        Value: `${OrgLevelTypeEnum.Site},${OrgLevelTypeEnum.Function},${OrgLevelTypeEnum.BusinessUnit},${OrgLevelTypeEnum.Corporate}`
      })
    });
    this.isFormLoading = true;
    forkJoin({
      entity: this.getEntity(),
      organizationLevels: this.organizationLevelService.getAll(orgPayload),
      users: this.userService.getAll(),
    }).subscribe({
      next: result => {
        this.entity = result.entity;
        this.organizationLevels = result.organizationLevels;
        this.users = result.users.map(u => ({ id: u.id, name: (u as any).userName || u.name }));
        this.initModels()
        this.initFields();
        this.isFormLoading = false;
      },
      error: err => {
        this.toastService.showError(err);
        this.isFormLoading = false;
      }
    });
  }

  getNewObject(): CalibrationEquipment {
    return new CalibrationEquipment();
  }

  validate(): boolean {
    return true;
  }

  initModels() {
    if (this.entity.dateAcquired) this.entity.dateAcquired = this.dateFormatService.convertUTCDateStringToUserTimeZoneString(this.entity.dateAcquired);
    if (this.entity.calibrationDue) this.entity.calibrationDue = this.dateFormatService.convertUTCDateStringToUserTimeZoneString(this.entity.calibrationDue);
    if (this.entity.lastCalibrated) this.entity.lastCalibrated = this.dateFormatService.convertUTCDateStringToUserTimeZoneString(this.entity.lastCalibrated);
    this.entity.ownerUserIds = (this.entity.owners || []).map(o => o.ownerUserId);
    this._originalLastNotification = this.entity.lastDueSoonNotificationSentAt;
    if (this.entity.lastDueSoonNotificationSentAt) {
      const { date, time } = this.dateFormatService.utcToLocalDateAndTime(this.entity.lastDueSoonNotificationSentAt);
      this.entity.lastDueSoonNotificationSentAt = `${date} ${time}`;
    } else {
      this.entity.lastDueSoonNotificationSentAt = this.translateService.getDynamicTranslation('Never');
    }
  }

  initFields(): void {
    const enumLookup = (value) => this.typeService.getEnumOptions(value).map(option => ({
      id: option.id, name: option.name.replace(/([A-Z])/g, ' $1').trim()
    }));
    this.fields = [
      new TextFormField({
        property: nameOf<CalibrationEquipment>(x => x.modelNumber),
        name: 'Model Number'
      }),
      new TextFormField({
        property: nameOf<CalibrationEquipment>(x => x.serialNumber),
        name: 'Serial Number',
        required: false
      }),
      new TextFormField({
        property: nameOf<CalibrationEquipment>(x => x.equipmentCode),
        name: 'Equipment Code',
      }),
      new HierarchicalSelectionFormField({
        property: nameOf<CalibrationEquipment>(x => x.organizationLevelId),
        name: 'Organization',
        options: toOrganizationTree(this.organizationLevels as OrganizationLevel[]),
        required: true
      }),
      new TextFormField({
        property: nameOf<CalibrationEquipment>(x => x.manufacturer),
        name: 'Manufacturer',
        required: false
      }),
      new DateFormField({
        property: nameOf<CalibrationEquipment>(x => x.dateAcquired),
        name: 'Date Acquired'
      }),
      new SingleSelectionFormField({
        property: nameOf<CalibrationEquipment>(x => x.calibrationFrequency),
        name: 'Calibration Frequency',
        options: enumLookup(CalibrationFrequency)
      }),
      new SingleSelectionFormField({
        property: nameOf<CalibrationEquipment>(x => x.status),
        name: 'Status',
        options: enumLookup(EquipmentStatus)
      }),
      new DateFormField({
        property: nameOf<CalibrationEquipment>(x => x.lastCalibrated),
        name: 'Last Calibrated'
      }),
      new DateFormField({
        property: nameOf<CalibrationEquipment>(x => x.calibrationDue),
        name: 'Calibration Due'
      }),
      new NumberFormField({
        property: nameOf<CalibrationEquipment>(x => x.notifyBeforeDueDays),
        name: 'Notify Before Due Days',
        min: 0,
        max: 365,
        required: false
      }),
      new MultiSelectionFormField({
        property: nameOf<CalibrationEquipment>(x => x.ownerUserIds),
        name: 'Equipment Owners',
        options: this.users,
        required: false
      }),
      new TextFormField({
        property: nameOf<CalibrationEquipment>(x => x.lastDueSoonNotificationSentAt),
        name: 'Last Calibration Notification Sent',
        required: false,
        readonly: true,
        isVisible: () => !!this.entity.id
      }),
    ];
  }

  onBeforeSave(model: CalibrationEquipment): void {
    if (model.dateAcquired) model.dateAcquired = this.dateFormatService.convertUserTimeZoneStringToUTCDateString(model.dateAcquired);
    if (model.lastCalibrated) model.lastCalibrated = this.dateFormatService.convertUserTimeZoneStringToUTCDateString(model.lastCalibrated);
    if (model.calibrationDue) model.calibrationDue = this.dateFormatService.convertUserTimeZoneStringToUTCDateString(model.calibrationDue);
    model.owners = (model.ownerUserIds || []).map(userId => {
      const owner = new CalibrationEquipmentOwner();
      owner.ownerUserId = userId;
      return owner;
    });
    model.lastDueSoonNotificationSentAt = this._originalLastNotification;
  }
}
